import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/styles';
import { IconButton, Theme, Modal, Grid, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import EditProfileForm from './components/EditProfileForm';
import { isValidEmail } from '../../utils';
import axios, { CancelTokenSource } from 'axios';
import { GET_EDIT_USER_URL } from 'constants/url';

interface Props {
  open: boolean;
  userId: number;
  userDisplayName: string;
  userLoginName: string;
  handleCancel(): void;
  setOpenSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarVarient: React.Dispatch<React.SetStateAction<'success' | 'error'>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 4
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}));

const UpdateProfileModal: React.FC<Props> = props => {
  const classes = useStyles();
  let cancelTokenSource: CancelTokenSource;

  const { userId, userDisplayName, userLoginName, open, handleCancel, setOpenSnackbar, setSnackbarVarient } = props;
  const [isLoading, setLoading] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const [emailError, setEmailError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const resetFormValues = useCallback(() => {
    if (!userId) {
      return;
    }

    const displayName = userDisplayName;
    const email = userLoginName;
    
    setEmail(email);
    setName(displayName);
  }, [userId, userDisplayName, userLoginName]);

  // This to ensure the form value and errors are reset/cleared when selectedUser changes
  // resetFormValues will be modified everytime user changes, due to useCallback
  useEffect(() => {
    resetFormValues();
    clearFormErrors();
  }, [resetFormValues]);

  const clearFormErrors = () => {
    setEmailError('');
    setNameError('');
    setPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  // This is to ensure that the form vale and erors are reset/cleared when user canceled the editing
  const handleOnClose = () => {
    resetFormValues();
    clearFormErrors();
    handleCancel();
  };

  const validateForm = () => {
    let ret = true;
    clearFormErrors();

    if (!email || !email.trim()) {
      setEmailError('Please enter email');
      ret = false;
    } else if (!isValidEmail(email)) {
      setEmailError('Please enter an valid email');
      ret = false;
    }

    if (!name || !name.trim()) {
      setNameError('Please enter display name');
      ret = false;
    }

    if (password !== confirmPassword) {
      setPasswordError('Password and confirm password is different');
      ret = false;
    }

    return ret;
  };

  const handleOnSubmit: React.FormEventHandler = async event => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      cancelTokenSource = axios.CancelToken.source();
      await axios.put(
        `${GET_EDIT_USER_URL(userId)}`,
        {
          displayName: name,
          email,
          newPassword: password ? password : undefined
        },
        { cancelToken: cancelTokenSource.token }
      );

      setOpenSnackbar(true);
      setSnackbarVarient('success');
      handleCancel();
    } catch (err) {
      console.log(err);
      const { errorCode } = err.data;

      if (errorCode === 7) {
        setOpenSnackbar(true);
        setSnackbarVarient('error');
        setEmailError('User is duplicated.');
      }

      if (errorCode === 4) {
        setOpenSnackbar(true);
        setSnackbarVarient('error');
        setPasswordError('Password harus berisi (A-Z dan a-z), nomor (1-9) dan berisi 8 atau lebih karakter');
      }
    }

    setLoading(false);
  };

  return (
    <Modal aria-labelledby='modal-title' open={open} disableBackdropClick={true}>
      <Grid container item xs={8} sm={8} md={8} lg={5} xl={5} spacing={3} direction='column' className={classes.paper}>
        <Typography variant='h4' id='modal-title' color='primary' align='center'>
          My Profile
        </Typography>
        <IconButton size='small' className={classes.closeButton} onClick={handleOnClose}>
          <CloseIcon />
        </IconButton>
        <EditProfileForm
          email={email}
          setEmail={setEmail}
          emailError={emailError}
          name={name}
          setName={setName}
          nameError={nameError}
          password={password}
          setPassword={setPassword}
          passwordError={passwordError}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          isSubmitting={isLoading}
          onSubmit={handleOnSubmit}
          onCancel={handleOnClose}
        />
      </Grid>
    </Modal>
  );
};

export default UpdateProfileModal;
